import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale/pt-BR'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'
import { z } from 'zod'
import { api } from './api'

/**
 * Hora de parede da terapeuta.
 *
 * Este é o único arquivo do web que importa `date-fns-tz` e a locale pt-BR.
 * A agenda é escrita em hora local ("terça às 9h") e o servidor fala em ISO
 * com fuso: toda conversão entre os dois passa por aqui, senão cada tela
 * inventa a própria e uma delas erra no dia em que o país mexer no horário.
 *
 * `toZonedTime` devolve um `Date` cujos componentes locais são a hora de
 * parede do fuso pedido. É por isso que o `format` do date-fns, que lê os
 * componentes locais, produz o texto certo.
 */

export const DEFAULT_TZ = 'America/Sao_Paulo'

export const MINUTE_MS = 60_000
export const HOUR_MS = 3_600_000
export const DAY_MS = 86_400_000

const meSchema = z.object({ tz: z.string().min(1).optional() })

/**
 * O fuso do consultório, lido de `GET /api/me`.
 *
 * O paciente recebe o fuso da terapeuta dele, não o do próprio aparelho: o
 * horário combinado é o do consultório, e quem abre o portal viajando precisa
 * ver o mesmo "terça às 9h" que viu quando marcou.
 */
export function useTimeZone(): string {
  const { data } = useQuery({
    queryKey: ['me', 'tz'],
    queryFn: () => api('/api/me', { schema: meSchema }),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    // Fuso errado é pior que fuso ausente: se a chamada falhar, a tela não
    // pisca, fica em Brasília e segue.
    throwOnError: false,
  })
  return data?.tz ?? DEFAULT_TZ
}

/**
 * Fuso deste aparelho. Só serve para avisar quando ele difere do consultório:
 * a agenda nunca desenha nele.
 */
export function navegadorTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ
  } catch {
    return DEFAULT_TZ
  }
}

/** Nome curto do fuso ("BRT", "GMT-3"), para o aviso de fuso diferente. */
export function siglaDoFuso(tz: string, at: number = Date.now()): string {
  try {
    const parte = new Intl.DateTimeFormat('pt-BR', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(at)
      .find((item) => item.type === 'timeZoneName')
    return parte?.value ?? tz
  } catch {
    return tz
  }
}

/** O instante visto como hora de parede do fuso, pronto para o `format`. */
function wall(at: number | Date, tz: string): Date {
  return toZonedTime(at, tz)
}

/** Instante de uma hora de parede `YYYY-MM-DDTHH:mm`. */
export function instantOf(localDateTime: string, tz: string): number {
  return fromZonedTime(localDateTime, tz).getTime()
}

/** Instante de uma data local mais `HH:mm`. */
export function dayTimeToInstant(date: string, time: string, tz: string): number {
  return instantOf(`${date}T${time}`, tz)
}

/** Data local do instante, `YYYY-MM-DD`. */
export function localDateOf(at: number | Date, tz: string): string {
  return format(wall(at, tz), 'yyyy-MM-dd')
}

/** Data e hora locais do instante, `YYYY-MM-DDTHH:mm`. */
export function localDateTimeOf(at: number | Date, tz: string): string {
  return format(wall(at, tz), "yyyy-MM-dd'T'HH:mm")
}

/** Horário em `HH:mm`. Vai sempre dentro de `.num`. */
export function formatHora(at: number | Date, tz: string): string {
  return format(wall(at, tz), 'HH:mm')
}

/** Dia e mês em `dd/MM`. */
export function formatDia(at: number | Date, tz: string): string {
  return format(wall(at, tz), 'dd/MM')
}

/** Data por extenso: `19 de setembro de 2026`. */
export function formatDataLonga(at: number | Date, tz: string): string {
  return format(wall(at, tz), "d 'de' MMMM 'de' yyyy", { locale: ptBR })
}

/** Dia da semana por extenso, minúsculo: `sexta-feira`. */
export function formatDiaSemana(at: number | Date, tz: string): string {
  return format(wall(at, tz), 'EEEE', { locale: ptBR })
}

/** Abreviação de três letras usada no cabeçalho da grade: `seg`. */
export function formatDiaCurto(at: number | Date, tz: string): string {
  return format(wall(at, tz), 'EEEEEE', { locale: ptBR }).replace('.', '')
}

/** Data e hora juntas, do jeito que a terapeuta lê: `19/09 às 14:00`. */
export function formatDataHora(at: number | Date, tz: string): string {
  return `${formatDia(at, tz)} às ${formatHora(at, tz)}`
}

/** 0 domingo, 6 sábado. É a mesma numeração das regras do `@bia/domain`. */
export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay()
}

/** Soma dias a uma data local sem passar por fuso nenhum. */
export function addDays(date: string, days: number): string {
  const at = new Date(`${date}T00:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}

/**
 * Segunda-feira da semana de uma data local. A grade começa na segunda
 * porque é assim que a semana de trabalho é lida aqui, mesmo com as regras
 * de disponibilidade numerando o domingo como zero.
 */
export function startOfWeek(date: string): string {
  const weekday = weekdayOf(date)
  return addDays(date, weekday === 0 ? -6 : 1 - weekday)
}

/** As sete datas locais da semana que começa em `monday`. */
export function weekDates(monday: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index))
}

/** Duração em minutos a partir de dois instantes. */
export function minutesBetween(from: number, to: number): number {
  return Math.round((to - from) / MINUTE_MS)
}

/** `50 min`, `1 h`, `1 h 30 min`. */
export function formatDuracao(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}

/**
 * Quanto tempo passou, em texto curto. Serve o "recebido há" da lista de
 * solicitações, onde a data cheia não ajuda e o relógio de parede sim.
 */
export function formatHaQuanto(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000))
  if (seconds < 60) return 'agora'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h`
  const days = Math.round(hours / 24)
  return days === 1 ? '1 dia' : `${days} dias`
}
